import { FC, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material';
import { GraphVisualization, GraphControls } from '@/components/Graph';
import api from '@/services/api';
import type { SwitchQueryResponse, VersionSummary, GraphData, GraphNode, GraphEdge } from '@/types/entities';

interface HistoricalVersionModalProps {
  open: boolean;
  onClose: () => void;
  hostname: string;
  version: VersionSummary;
  currentVersionId: string;
}

export const HistoricalVersionModal: FC<HistoricalVersionModalProps> = ({
  open,
  onClose,
  hostname,
  version,
  currentVersionId,
}) => {
  const navigate = useNavigate();
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switchData, setSwitchData] = useState<SwitchQueryResponse | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const isCurrentVersion = version.transactionId === currentVersionId;

  useEffect(() => {
    if (open) {
      fetchHistoricalData();
    }
  }, [open, version.transactionId]);

  const fetchHistoricalData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSwitchAtVersion(hostname, version.transactionId);
      setSwitchData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load historical data');
    } finally {
      setLoading(false);
    }
  };

  // Convert SwitchQueryResponse to GraphData format
  const convertToGraphData = (data: SwitchQueryResponse): GraphData => {
    const nodes: GraphNode[] = [];
    const edges: GraphEdge[] = [];

    // Add switch node
    nodes.push({
      id: data.switch.hostname,
      label: data.switch.hostname,
      type: 'switch',
      data: data.switch,
    });

    // Add interface nodes
    data.interfaces.forEach((iface) => {
      nodes.push({
        id: iface.uniqueId,
        label: iface.name,
        type: 'interface',
        data: iface,
      });

      // Add HAS_INTERFACE edge
      edges.push({
        id: `${data.switch.hostname}-${iface.uniqueId}`,
        source: data.switch.hostname,
        target: iface.uniqueId,
        type: 'HAS_INTERFACE',
        label: '',
      });
    });

    // Add VLAN nodes
    data.vlans.forEach((vlan) => {
      nodes.push({
        id: `vlan-${vlan.vlanID}`,
        label: vlan.name || `VLAN ${vlan.vlanID}`,
        type: 'vlan',
        data: vlan,
      });
    });

    // Add relationship edges
    data.relationships.forEach((rel) => {
      edges.push({
        id: `${rel.fromInterface}-${rel.toVlan}-${rel.type}`,
        source: rel.fromInterface,
        target: `vlan-${rel.toVlan}`,
        type: rel.type,
        label: rel.type === 'MEMBER_OF' ? 'member' : 'trunk',
        data: { native: rel.properties.native },
      });
    });

    return { nodes, edges };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          bgcolor: isCurrentVersion ? 'background.default' : 'grey.50',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon color={isCurrentVersion ? 'primary' : 'warning'} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">
            {isCurrentVersion ? 'Current Version' : 'Historical Version'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Switch: {hostname} • {new Date(version.uploadTimestamp).toLocaleString()}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Warning Banner for Historical Data */}
        {!isCurrentVersion && (
          <Alert severity="warning" icon={<HistoryIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>You are viewing a historical configuration.</strong> This is NOT the current state.
              This configuration was active from {new Date(version.uploadTimestamp).toLocaleString()}.
            </Typography>
          </Alert>
        )}

        {/* Version Info */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Transaction ID:</strong> {version.transactionId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Uploaded by:</strong> {version.uploaderUsername}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Configuration:</strong> {version.interfaceCount} interfaces, {version.vlanCount} VLANs
          </Typography>
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Graph" />
          <Tab label="Interfaces" />
          <Tab label="VLANs" />
          <Tab label="Properties" />
        </Tabs>

        {/* Tab Content */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading historical configuration...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {switchData && (
          <Box sx={{ minHeight: 400 }}>
            {activeTab === 0 && (
              <Box sx={{ position: 'relative' }}>
                <GraphVisualization
                  ref={graphContainerRef}
                  data={convertToGraphData(switchData)}
                  layout="breadthfirst"
                  height="600px"
                  centerNodeId={switchData.switch.hostname}
                />
                <GraphControls containerRef={graphContainerRef} />
              </Box>
            )}

            {activeTab === 1 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Interface</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>MTU</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {switchData.interfaces.map((iface) => (
                      <TableRow key={iface.uniqueId}>
                        <TableCell>{iface.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={iface.mode}
                            size="small"
                            color={iface.mode === 'trunk' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={iface.status || 'unknown'}
                            size="small"
                            color={iface.status === 'up' ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>{iface.mtu || 'N/A'}</TableCell>
                        <TableCell>{iface.description || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {activeTab === 2 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>VLAN ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {switchData.vlans.map((vlan) => (
                      <TableRow key={vlan.vlanID}>
                        <TableCell>{vlan.vlanID}</TableCell>
                        <TableCell>{vlan.name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={vlan.status || 'active'}
                            size="small"
                            color={vlan.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {activeTab === 3 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Switch Properties</Typography>
                <Typography variant="body2"><strong>Hostname:</strong> {switchData.switch.hostname}</Typography>
                <Typography variant="body2"><strong>Vendor:</strong> {switchData.switch.vendor || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Model:</strong> {switchData.switch.model || 'N/A'}</Typography>
                <Typography variant="body2"><strong>OS Version:</strong> {switchData.switch.osVersion || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Management IP:</strong> {switchData.switch.managementIp || 'N/A'}</Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Statistics:</strong> {switchData.statistics.port_count} ports, {switchData.statistics.vlan_count} VLANs
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          {!isCurrentVersion && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate(`/switch/${hostname}/compare?versionA=${version.transactionId}&versionB=${currentVersionId}&autorun=true`);
                  onClose();
                }}
              >
                Compare with Current
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/switch/${hostname}/compare?versionA=${version.transactionId}`);
                  onClose();
                }}
              >
                Compare with Another Version
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
